import { type BarChartFactory, BarChart as MantineBarChart } from "@mantine/charts";
import { componentWrapper } from "../../utilities";
import styles from "./bar-chart.module.scss";
import type { IProps } from "./bar-chart.types";
import { useMemo } from "react";
import { getBarPropsRadius } from "./bar-chart.utilities";

/**
 * Bar chart component for displaying data in a bar chart format.
 * @param {object} props.mouseEvents - Mouse event handlers for chart interactions.
 * @param {object} props.barChartProps - Additional props for the bar chart configuration.
 * @param {Array} props.series - Array of series data to display in the chart.
 * @param {string} props.type - The type of bar chart.
 * @param {boolean} props.hasCustomBar - Whether the chart uses custom bar rendering. When set, Mantine receives an empty
 * `series` so Recharts only reserves space in each category for the consumer's own `Bar` children.
 * @param {Array} props.data - Array of data points for the chart.
 * @returns {JSX.Element} The BarChart component.
 */
const BarChart = componentWrapper<BarChartFactory, IProps>("BarChart", (props, ref) => {
    const { mouseEvents, barChartProps, series, type, hasCustomBar, data, ...otherProps } = props;

    // Mantine maps over `data` without guarding it, so a consumer that passes undefined - typically data from a
    // request that failed or has not resolved - throws and takes down the whole page instead of just this chart.
    const chartData = data ?? [];

    // Hiding the series bars with CSS is not enough: Recharts still lays them out, so every category is split between
    // the invisible series bars and the consumer's custom bars, which squeezes the visible bars down to hairlines when
    // the categories are narrow. Giving Mantine an empty series means Recharts only sizes the custom bars.
    const renderedSeries = hasCustomBar ? [] : series;

    const combinedBarChartProps = useMemo(
        () => ({
            ...barChartProps,
            ...mouseEvents,
        }),
        [barChartProps, mouseEvents]
    );

    return (
        <MantineBarChart
            ref={ref}
            classNames={{
                root: styles["container"],
                axisLabel: styles["axis-label"],
                axis: styles["axis"],
                bar: styles["bar"],
            }}
            withTooltip={chartData.length > 0}
            data={chartData}
            data-type={type || "default"}
            data-has-custom={hasCustomBar ? "true" : "false"}
            type={type}
            series={renderedSeries}
            {...otherProps}
            barChartProps={{ barGap: 1, barCategoryGap: 8, ...combinedBarChartProps }}
            barProps={(seriesItem) => {
                return typeof props.barProps === "function"
                    ? props.barProps(seriesItem)
                    : {
                          radius: getBarPropsRadius(seriesItem, series, type || "default"),
                          className: styles["bar"],
                          ...props.barProps,
                      };
            }}
        />
    );
});

export { BarChart };

// TODO:
// - Revisit this later: Colours for series to always appear in the specific order
